/** sessionStorage handshake between the boot-time chunk-failure auto-reload in
 *  main.tsx and the Sentry setup in config/sentry.js.
 *
 *  The auto-reload catch in main.tsx fires when the bootstrap bundle - which
 *  contains Sentry itself - failed to load, so nothing can be captured at that
 *  moment. Instead the failure details are parked here and turned into a
 *  Sentry event on the next successful boot. If the retry also fails (the
 *  blocked-resource fallback), the record simply waits for whichever later
 *  page load succeeds.
 *
 *  This module must stay dependency-free: main.tsx imports it into the entry
 *  chunk, and importing Sentry here would drag the SDK into that chunk. */

const STORAGE_KEY = "viraly-chunk-reload-telemetry";

export interface ChunkLoadFailureRecord {
  /** The import error message; browsers include the failed chunk URL in it
   *  ("Failed to fetch dynamically imported module: https://..."). */
  message: string;
  /** ISO timestamp of when the auto-reload was issued. */
  reloadedAt: string;
}

export function recordChunkLoadFailure(error: unknown): void {
  try {
    const record: ChunkLoadFailureRecord = {
      message: error instanceof Error ? error.message : String(error),
      reloadedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // sessionStorage unavailable or full - telemetry must never block the reload.
  }
}

/** Read-and-clear. Returns null when no auto-reload preceded this boot. */
export function consumeChunkLoadFailure(): ChunkLoadFailureRecord | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(STORAGE_KEY);
    const parsed: unknown = JSON.parse(raw);
    const record = parsed as Partial<ChunkLoadFailureRecord> | null;
    if (typeof record?.message !== "string") {
      return null;
    }
    return {
      message: record.message,
      reloadedAt:
        typeof record.reloadedAt === "string" ? record.reloadedAt : "",
    };
  } catch {
    return null;
  }
}
